import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Keyboard, ScrollView, Image, ActivityIndicator } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import { cloneDeep } from 'lodash';
import { toast } from 'react-hot-toast';
import Lightbox from 'yet-another-react-lightbox';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import imageCompression from 'browser-image-compression';

// Child Components
import SectionContainer from './component_section_container';
import EditNameModal from '../Profile/component_name_edit';

// Custom Components
import { Button, CityPickerModal, CuisinePickerModal, NavBar, TextBox } from '../../components';

// Utils
import { checkEmail, checkValidURL, getSocialLinkID, showAlert, showAppError, validateUSNumber } from '../../utils/util_helpers';
import { getCoverPhoto, getLogoPhoto } from '../../utils/util_truck_helpers';

// APIs
import { updateTruckCover, updateTruckLogo, updateTruckProfile } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { setGlobalState, STATE_KEYS } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

export function ProfileEdit() {
    // region Route Params
    const {
        truckData = {},
        onProfileUpdated = () => {}
    } = useRoute()?.params || {};
    // endregion

    // region Refs
    const cuisinePickerModal = useRef(null);
    const cityPickerModal = useRef(null);
    const editNameModal = useRef(null);
    const logoPicker = useRef(null);
    const coverPicker = useRef(null);
    // endregion

    // region Local State
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedCuisines, setSelectedCuisines] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [licenseNeededCities, setLicenseNeededCities] = useState([]);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [website, setWebsite] = useState('');
    const [facebook, setFacebook] = useState('');
    const [twitter, setTwitter] = useState('');
    const [instagram, setInstagram] = useState('');
    const [currentLogo, setCurrentLogo] = useState(null);
    const [logoOpened, setLogoOpened] = useState(false);
    const [isLoadingLogo, setLoadingLogo] = useState(false);
    const [currentCover, setCurrentCover] = useState(null);
    const [coverOpened, setCoverOpened] = useState(false);
    const [isLoadingCover, setLoadingCover] = useState(false);
    // endregion

    // region Component Mounting
    useEffect(() => {
        // Fill existing info
        setName(truckData.name || '');
        setDescription(truckData.company_description || '');
        setPhone(truckData.phone?.replace('+1', '') || '');
        setEmail(truckData.contact_email || '');
        setWebsite(truckData.website_url || '');
        setCurrentLogo(getLogoPhoto(truckData));
        setCurrentCover(getCoverPhoto(truckData, undefined, true));

        // Parse cuisine list
        const cuisineList = [];
        (truckData.cuisine || [])?.forEach((cuisine) => {
            const cuisineIndex = GLOBAL.CUISINE_LIST.findIndex((cuisineWithID) => (
                (cuisineWithID.slug === cuisine.slug) ||
                (cuisineWithID.name?.toLowerCase() === cuisine.name?.toLowerCase())
            ));
            if (cuisineIndex > -1) cuisineList.push(GLOBAL.CUISINE_LIST[cuisineIndex]);
        });
        setSelectedCuisines(cuisineList);

        // Parse serving cities
        const cityIDs = truckData.city.map((item) => item.id);
        setSelectedCities(cityIDs)
        setLicenseNeededCities(
            truckData.city
                .filter((city) => GLOBAL.CITIES[city.id].requireLicenseNumber)
                .map((city) => ({ ...cloneDeep(city), value: city['license_number'] || '' }))
        );

        // Parse the social links
        setFacebook(truckData.facebook_url ? getSocialLinkID(truckData.facebook_url) : '');
        setTwitter(truckData.twitter_url ? getSocialLinkID(truckData.twitter_url) : '');
        setInstagram(truckData.instagram_url ? getSocialLinkID(truckData.instagram_url) : '');
    }, []);
    // endregion

    // region Function - Get License Needed Cities
    const getLicenseNeededCities = (cityIDs) => cityIDs
        .filter((id) => GLOBAL.CITIES[id]?.requireLicenseNumber)
        .map((id) => ({
            ...cloneDeep(GLOBAL.CITIES[id]),
            value: licenseNeededCities.find((city) => city.id === id)?.['value'] || ''
        }));
    // endregion

    // region Function - Go Upload Logo
    const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
    const goUploadLogo = (newLogoFile) => {
        const resetLogo = () => setCurrentLogo(truckData.self_logo);
        setLoadingLogo(true);
        imageCompression(newLogoFile, imageCompressionOptions)
            .then((compressedFile) => {
                updateTruckLogo(truckData.id, compressedFile)
                    .then(() => onProfileUpdated())
                    .catch((error) => {
                        showAppError(error);
                        resetLogo();
                    })
                    .finally(() => setLoadingLogo(false));
            })
            .catch((error) => {
                showAppError(error.message);
                resetLogo();
            })
            .finally(() => setLoadingLogo(false));
    }
    // endregion

    // region Function - Go Upload Cover
    const goUploadCover = (newCoverFile) => {
        setLoadingCover(true);
        const resetCover = () => setCurrentCover(truckData['cover_photos']?.pop()?.path);
        imageCompression(newCoverFile, imageCompressionOptions)
            .then((compressedFile) => {
                updateTruckCover(truckData.id, compressedFile)
                    .then(() => onProfileUpdated())
                    .catch((error) => {
                        showAppError(error);
                        resetCover();
                    })
                    .finally(() => setLoadingCover(false));
            })
            .catch((error) => {
                showAppError(error.message);
                resetCover();
            })
            .finally(() => setLoadingCover(false));
    }
    // endregion

    // region Function - Go Update Truck Profile
    const goUpdateTruckProfile = () => {
        Keyboard.dismiss();

        // Check if at least one cuisine was selected
        if (selectedCuisines.length <= 0) {
            showAlert({ text: 'You must select at least 1 cuisine type for your food truck before proceeding' });
            return;
        }

        // Check if all required fields are completed
        if (!email || !website || !phone) {
            showAlert({ text: 'Please fill in all required information (marked with red asterisks) before proceeding' });
            return;
        }

        // Check if email is in correct format
        if (!checkEmail(email)) {
            showAlert({ text: 'Please enter a valid email format' });
            return;
        }

        // Check if phone is in correct format
        if (!validateUSNumber(phone)) {
            showAlert({ text: 'Please enter a valid US phone number format' });
            return;
        }

        // Check if website is in correct format
        if (!checkValidURL(website)) {
            showAlert({ text: 'Please enter a valid website URL format' });
            return;
        }

        // Check if all required license numbers are input
        if (
            !!licenseNeededCities.length &&
            !!licenseNeededCities.filter((city) => !city.value?.trim()).length
        ) {
            showAlert({ text: 'Please enter all required mobile food license numbers' });
            return;
        }

        // Generate Data
        const truckCuisineStr = selectedCuisines.map((cuisine) => cuisine.id).join(',');
        const websiteStr = website?.toLowerCase() || '';
        const newTruckData = {
            company_description: description,
            cuisine: truckCuisineStr,

            contact_email: email,
            phone: `+1${phone}`,
            website_url: websiteStr.startsWith('http') ? websiteStr : `https://${websiteStr}`,

            facebook_url: facebook ? ('https://facebook.com/' + getSocialLinkID(facebook)) : '',
            instagram_url: instagram ? ('https://instagram.com/' + getSocialLinkID(instagram)) : '',
            twitter_url: twitter ? ('https://twitter.com/' + getSocialLinkID(twitter)) : '',

            city: selectedCities.map((cityID) => cityID).join(','),

            // To be parsed in API call
            licenseNumberArr: licenseNeededCities.map((city) => ({ id: city.id, value: city.value }))
        };

        // Call API
        setGlobalState(STATE_KEYS.LOADING_CONFIG, { loadingText: 'Updating Profile...' });
        updateTruckProfile(truckData.id, newTruckData)
            .then(() => {
                onProfileUpdated();
                RootNavigation.goBack();
                toast('Truck profile successfully updated');
            })
            .catch((error) => showAppError(error))
            .finally(() => setGlobalState(STATE_KEYS.LOADING_CONFIG, null));
    }
    // endregion

    // region Function - Has Changes
    const hasChanges = () => {
        // Old Values
        const oldCuisineStr = truckData.cuisine.map((cuisine) => cuisine.slug).sort((a, b) => a.localeCompare(b)).join(',');
        const oldCityStr = truckData.city.map((city) => city.slug).sort((a, b) => a.localeCompare(b)).join(',');
        const oldLicenseNeededStr = truckData.city
            .filter((city) => city['require_license_number'])
            .map((city) => city['license_number'])
            .sort((a, b) => a.localeCompare(b)).join(',');

        // New Values
        const cuisineStr = selectedCuisines.map((cuisine) => cuisine.slug).sort((a, b) => a.localeCompare(b)).join(',');
        const cityStr = selectedCities.map((cityID) => GLOBAL.CITIES[cityID]?.slug).sort((a, b) => a.localeCompare(b)).join(',');
        const licenseNeededStr = licenseNeededCities.map((city) => city.value).sort((a, b) => a.localeCompare(b)).join(',');

        return(
            cuisineStr.localeCompare(oldCuisineStr) ||
            cityStr.localeCompare(oldCityStr) ||
            licenseNeededStr.localeCompare(oldLicenseNeededStr) ||
            description.localeCompare(truckData.company_description || '') ||
            email.localeCompare(truckData['contact_email'] || '') ||
            phone.replace('+1', '').localeCompare(truckData['phone']?.replace('+1', '') || '') ||
            website.replace('http://', '').replace('https://', '').localeCompare(truckData['website_url']?.replace('http://', '').replace('https://', '')) ||
            getSocialLinkID(facebook).localeCompare(getSocialLinkID(truckData['facebook_url'])) ||
            getSocialLinkID(twitter).localeCompare(getSocialLinkID(truckData['twitter_url'])) ||
            getSocialLinkID(instagram).localeCompare(getSocialLinkID(truckData['instagram_url']))
        );
    }
    // endregion

    // region Render Truck Info

    // region Render Required License Number Cities
    const renderRequiredLicenseNumberCities = () => (
        !!licenseNeededCities?.length &&
        <View style={{ alignSelf: 'stretch', marginTop: 24 }}>
            <Text style={{ color: GLOBAL.COLOR.BASIC.UI_BLUE, letterSpacing: 0.5, fontWeight: 'bold', fontSize: 16 }}>
                LICENSE NUMBERS <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            <Text style={{ marginTop: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 18, marginBottom: 8 }}>
                You have selected one or more cities that require declaration of mobile food license number(s), please enter them below
            </Text>

            {/* Input */}
            {
                licenseNeededCities.map((city, index) => (
                    <TextBox
                        key={city.id}
                        title={`${city.name}, ${city.state.short_name}`}
                        onTitlePressed={() => showAlert({ text: `${city.name} (${city.state.short_name}) mobile food license number is required` })}
                        placeholder={`License Number for ${city.name}, ${city.state.short_name}`}
                        returnKeyType={'done'}
                        autoCorrect={false}
                        style={{ marginTop: 0, flex: 1 }}
                        textInputStyle={{ fontWeight: '500' }}
                        onChangeText={(text) => {
                            const tempArr = cloneDeep(licenseNeededCities);
                            tempArr.splice(index, 1, { ...licenseNeededCities[index], value: text });
                            setLicenseNeededCities(tempArr);
                        }}
                        value={licenseNeededCities[index]?.value}
                        onSubmitEditing={() => Keyboard.dismiss()}
                        onBlur={() => setLicenseNeededCities(licenseNeededCities)}
                    />
                ))
            }
        </View>
    )
    // endregion

    const renderTruckInfo = () => (
        <SectionContainer title={'Truck Info'}>
            {/* Truck Name */}
            <TextBox
                title={'Truck Name'}
                onPress={() => editNameModal.current?.showModal()}
                value={name}
            />

            {/* Cuisine Input */}
            <TextBox
                title={'Serving Cuisines'}
                placeholder={'Tap to select serving cuisines'}
                onPress={() => cuisinePickerModal.current?.showModal()}
                value={selectedCuisines.length > 0 ? selectedCuisines.map((item) => item.name).join(', ') : ''}
            />

            {/* City Input */}
            <TextBox
                title={'Serving Cities'}
                placeholder={'Tap to select serving cities'}
                onPress={() => cityPickerModal.current?.showModal()}
                value={selectedCities.length > 0 ? selectedCities.map((id) => GLOBAL.CITIES[id]?.name).join(', ') : ''}
            />

            {/* License Needed Cities */}
            {renderRequiredLicenseNumberCities()}
        </SectionContainer>
    );
    // endregion

    // region Render Introduction
    const renderIntroduction = () => (
        <SectionContainer
            style={{ marginTop: 24 }}
            title={'Introduction'}
            description={'Tell your customers about your awesome food truck'}>
            <TextBox
                contentBoxStyle={{ height: 300 }}
                title={'Description'}
                placeholder={'Our food truck started in 1915...'}
                multiline={true}
                onChangeText={setDescription}
                value={description}
            />
        </SectionContainer>
    );
    // endregion

    // region Render Contacts
    const renderContacts = () => (
        <SectionContainer
            style={{ marginTop: 24 }}
            title={'Contacts'}
            description={'Provide the best number and email address for your '}
            boldDescription={'public profile'}>
            {/* Website */}
            <TextBox
                title={'Website'}
                placeholder={'Website'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={() => Keyboard.dismiss()}
                onChangeText={setWebsite}
                value={website}
                showError={!!website && !checkValidURL(website)}
            />

            {/* Email */}
            <TextBox
                title={'Email Address'}
                placeholder={'Email Address'}
                keyboardType={'email-address'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={() => Keyboard.dismiss()}
                onChangeText={setEmail}
                value={email}
                showError={!checkEmail(email)}
            />

            {/* Phone */}
            <TextBox
                title={'Phone Number'}
                placeholder={'Phone Number'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                keyboardType={'number-pad'}
                onSubmitEditing={() => Keyboard.dismiss()}
                onChangeText={setPhone}
                value={phone}
                useMaskedInput={true}
                showError={phone?.trim() ? !validateUSNumber(phone) : false}
            />
        </SectionContainer>
    );
    // endregion

    // region Render Truck Info
    const renderSocial = () => (
        <SectionContainer
            style={{ marginTop: 24 }}
            title={'Social'}
            description={`You only need to enter your social network platform's ID, not the full URL. For example: `}
            boldDescription={'gotruckster'}>
            {/* Facebook */}
            <TextBox
                customImage={require('../../images/iconSocialFacebook@2x.png')}
                title={'Facebook'}
                placeholder={'Facebook'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={(text) => {
                    Keyboard.dismiss();
                    setFacebook(getSocialLinkID(text));
                }}
                onChangeText={setFacebook}
                value={facebook}
            />

            {/* Twitter */}
            <TextBox
                customImage={require('../../images/iconSocialTwitter.png')}
                title={'Twitter'}
                placeholder={'Twitter'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={(text) => {
                    Keyboard.dismiss();
                    setTwitter(getSocialLinkID(text));
                }}
                onChangeText={setTwitter}
                value={twitter}
            />

            {/* Instagram */}
            <TextBox
                customImage={require('../../images/iconSocialInstagram.png')}
                title={'Instagram'}
                placeholder={'Instagram'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={(text) => {
                    Keyboard.dismiss();
                    setInstagram(getSocialLinkID(text));
                }}
                onChangeText={setInstagram}
                value={instagram}
            />
        </SectionContainer>
    );
    // endregion

    // region Render Photo Loading Layout
    const renderPhotoLoadingView = () => (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
            <ActivityIndicator color={'white'} size={40} />
        </View>
    );
    // endregion

    // region Render Logo Picker
    const renderLogoPicker = () => (
        <>
            <input
                id={'logoPicker'}
                type={'file'}
                accept={'.png, .jpg, .jpeg'}
                ref={logoPicker}
                style={{ display: 'none' }}
                onChange={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    const file = event.target.files[0];
                    if (file) {
                        goUploadLogo(file);
                        setCurrentLogo(URL.createObjectURL(file));
                    }
                }}
            />

            {/* Logo */}
            <View style={{ flex: 1, backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 24 }}>
                <TouchableOpacity onPress={() => setLogoOpened(true)}>
                    <View style={{ width: logoSize, height: logoSize, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: logoSize / 2, overflow: 'hidden' }}>
                        <Image
                            source={currentLogo ? { uri: currentLogo } : require('../../images/image_placeholder_logo.png')}
                            style={{ width: logoSize, height: logoSize }}
                            resizeMode={'cover'}
                        />

                        {isLoadingLogo && renderPhotoLoadingView()}
                    </View>
                </TouchableOpacity>
            </View>

            {/* Picker Button */}
            <Button
                text={'Choose New Logo'}
                onPress={() => logoPicker.current?.click()}
                style={{ backgroundColor: 'white', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER, marginTop: 8 }}
                textColor={'black'}
                locked={isLoadingLogo}
            />
        </>
    )
    // endregion

    // region Function - Render Cover Picker
    const renderCoverPicker = () => (
        <>
            <input
                id={'coverPicker'}
                type={'file'}
                accept={'.png, .jpg, .jpeg'}
                ref={coverPicker}
                style={{ display: 'none' }}
                onChange={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    const file = event.target.files[0];
                    if (file) {
                        goUploadCover(file);
                        setCurrentCover(URL.createObjectURL(file));
                    }
                }}
            />

            {/* Cover */}
            <View style={{ flex: 1, backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 24, marginTop: 32 }}>
                <TouchableOpacity onPress={() => setCoverOpened(true)}>
                    <View style={{ width: logoSize, height: logoSize, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                        <Image
                            source={currentCover ? { uri: currentCover } : require('../../images/image_placeholder_cover.png')}
                            style={{ width: logoSize, height: logoSize }}
                            resizeMode={'cover'}
                        />

                        {isLoadingCover && renderPhotoLoadingView()}
                    </View>
                </TouchableOpacity>
            </View>

            {/* Cover Picker Button */}
            <Button
                text={'Choose New Cover Photo'}
                onPress={() => coverPicker.current?.click()}
                style={{ backgroundColor: 'white', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER, marginTop: 8 }}
                textColor={'black'}
                locked={isLoadingCover}
            />
        </>
    )
    // endregion

    // region MAIN RENDER
    const logoSize = 352;
    return(
        <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.MAIN_BACKGROUND }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => {
                    if (hasChanges()) {
                        showAlert({
                            text: 'You have made changes that are not yet saved, are you sure you want to continue?',
                            confirmButton: {
                                text: 'Discard Changes',
                                color: 'red',
                                onPress: () => RootNavigation.goBack()
                            },
                            cancelButton: { text: 'Stay Here' }
                        })
                    }
                    else RootNavigation.goBack();
                }}
                title={'Edit Truck Profile'}
                subTitle={hasChanges() ? 'Changes Not Saved' : ''}
                leftButtonIconName={'arrow-back'}
                rightButtonTitle={hasChanges() ? 'Save' : undefined}
                rightButtonOnPress={() => goUpdateTruckProfile()}
            />

            {/* Main Content */}
            <ScrollView style={{ flex: 1, width: '100%' }}>
                <View style={{ flex: 1, backgroundColor: 'transparent', padding: 24, flexDirection: 'row', alignItems: 'flex-start' }}>
                    {/* Left Column */}
                    <View style={{ flex: 1, marginRight: 24 }}>
                        {renderTruckInfo()}
                        {renderIntroduction()}
                        {renderContacts()}
                        {renderSocial()}
                    </View>

                    {/* Right Column */}
                    <View style={{ width: logoSize + 48, alignItems: 'center' }}>
                        {/* Logo Picker */}
                        {renderLogoPicker()}

                        {/* Cover Picker */}
                        {renderCoverPicker()}
                    </View>
                </View>
            </ScrollView>

            {/* Lightbox - Cover Photo */}
            <Lightbox
                open={coverOpened}
                close={() => setCoverOpened(false)}
                slides={[{ src: currentCover, title: 'Truck Cover Photo' }]}
                carousel={{ imageFit: 'contain', finite: true }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                render={{ buttonPrev: () => false, buttonNext: () => false }}
                captions={{ descriptionTextAlign: 'center' }}
            />

            {/* Lightbox - Logo Photo */}
            <Lightbox
                open={logoOpened}
                close={() => setLogoOpened(false)}
                slides={[{ src: currentLogo, title: 'Truck Logo Photo', description: 'To update your logo photo,\ngo to Edit Profile' }]}
                carousel={{ imageFit: 'contain', finite: true }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                render={{ buttonPrev: () => false, buttonNext: () => false }}
                captions={{ descriptionTextAlign: 'center' }}
            />

            {/* Cuisine Picker Modal */}
            <CuisinePickerModal
                ref={cuisinePickerModal}
                onSelectComplete={(cuisines) => setSelectedCuisines(cuisines)}
                selectedCuisines={selectedCuisines}
            />

            {/* City Selector Popover */}
            <CityPickerModal
                ref={cityPickerModal}
                onCitySelected={(selectedIDs) => {
                    setSelectedCities(selectedIDs);
                    setLicenseNeededCities(getLicenseNeededCities(selectedIDs));
                }}
                selectedCityIDs={selectedCities}
            />

            {/* Edit Name Modal */}
            <EditNameModal
                ref={editNameModal}
                truckData={truckData}
                onChangeCompleted={(newName) => {
                    setName(newName);
                    onProfileUpdated();
                }}
            />
        </View>
    );
    // endregion
}
